import { HabitSchedule, ScheduleFrequency } from '@griply/types';

import { getStoredEndDate } from './get-display-end-date';
import { hasScheduleDefinitionChanged } from './has-schedule-definition-changed';

export type ScheduleEditDraft = {
  startDate: Date;
  endDate: Date | null;
  completionTargetCount: number;
  startTime: number | null;
  duration: number | null;
  rrule: HabitSchedule['rrule'];
  frequency: ScheduleFrequency | null;
};

// rrule/frequency come from the draft, not the live schedule, so the save-time prompt sees a recurrence change.
export const buildUpdatedSchedule = (
  schedule: HabitSchedule,
  draft: ScheduleEditDraft,
): HabitSchedule => {
  const timeslot = {
    ...schedule.timeslot,
    startTime: draft.startTime,
    duration: draft.duration,
  };

  const updated: HabitSchedule = {
    ...schedule,
    rrule: draft.rrule,
    frequency: draft.frequency,
    startDate: draft.startDate,
    endDate: getStoredEndDate(draft.endDate),
    completionTarget: {
      ...schedule.completionTarget,
      count: draft.completionTargetCount,
    },
    timeslot,
  };

  // Stored occurrences describe the old pattern's days; the rrule re-supplies the new ones.
  if (hasScheduleDefinitionChanged(schedule, updated)) {
    return { ...updated, plannedOccurrences: null };
  }

  const timeslotChanged =
    (schedule.timeslot?.startTime ?? null) !== timeslot.startTime ||
    (schedule.timeslot?.duration ?? null) !== timeslot.duration;

  if (!timeslotChanged || !schedule.plannedOccurrences?.length) {
    return updated;
  }

  // A stored occurrence outranks schedule.timeslot on every read.
  return {
    ...updated,
    plannedOccurrences: schedule.plannedOccurrences.map((occurrence) => ({
      ...occurrence,
      timeslot,
    })),
  };
};
